/**
 * ReplacementDialog - Replacement availability dialog after issue report
 *
 * Shows two variants based on replacement availability:
 * 1. Replacement available: Green dialog with location info
 * 2. No replacement: Amber dialog with next steps
 *
 * CRITICAL: Large, clear buttons for warehouse use.
 */
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import type { PackingItemResult } from './ItemChecklist';

interface IssueReportResult {
  success: boolean;
  issueId: string;
  replacementAvailable: boolean;
  replacementSkuLocation?: string;
  replacementBinHint?: string;
  cxNotified: boolean;
}

interface ReplacementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  issueResult: IssueReportResult | null;
  item: PackingItemResult | null;
  onContinue: () => void;
}

export function ReplacementDialog({
  open,
  onOpenChange,
  issueResult,
  item,
  onContinue,
}: ReplacementDialogProps) {
  const { t } = useTranslation('production');

  if (!issueResult || !item) return null;

  const handleContinue = () => {
    onContinue();
    onOpenChange(false);
  };

  if (issueResult.replacementAvailable) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="w-6 h-6" aria-hidden="true" />
              {t('packer.replacement.titleAvailable')}
            </DialogTitle>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <p className="text-lg">
              {t('packer.replacement.available')} <strong>{item.sku}</strong> {t('packer.replacement.availableSuffix')}
            </p>

            <div className="bg-green-50 dark:bg-green-950 p-4 rounded-lg">
              <p className="text-xl font-bold">
                {issueResult.replacementSkuLocation || t('packer.replacement.checkStock')}
              </p>
              {issueResult.replacementBinHint && (
                <p className="text-muted-foreground mt-1">
                  {issueResult.replacementBinHint}
                </p>
              )}
            </div>

            <p className="text-muted-foreground">
              {t('packer.replacement.retrieveDesc')}
            </p>
          </div>

          <DialogFooter>
            <Button
              onClick={handleContinue}
              // CRITICAL: h-14 (56px) and full width for easy touch
              className="h-14 w-full text-lg"
            >
              {t('packer.replacement.gotIt')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600">
            <AlertTriangle className="w-6 h-6" aria-hidden="true" />
            {t('packer.replacement.titleUnavailable')}
          </DialogTitle>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <p className="text-lg">
            {t('packer.replacement.noStock')} <strong>{item.sku}</strong> {t('packer.replacement.noStockSuffix')}
          </p>

          <div className="bg-amber-50 dark:bg-amber-950 p-4 rounded-lg space-y-2">
            <p className="font-medium">{t('packer.replacement.nextSteps')}</p>
            <ol className="list-decimal list-inside space-y-1 text-sm">
              <li>{t('packer.replacement.continuePacking')}</li>
              <li>{t('packer.replacement.cxNotified')}</li>
              <li>{t('packer.replacement.orderHeld')}</li>
            </ol>
          </div>

          {issueResult.cxNotified && (
            <p className="text-sm text-green-600 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" aria-hidden="true" />
              {t('packer.replacement.slackNotified')}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            onClick={handleContinue}
            // CRITICAL: h-14 (56px) and full width for easy touch
            className="h-14 w-full text-lg"
          >
            {t('packer.replacement.continue')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export type { IssueReportResult };
